import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, FileText, MessageCircle, Printer, Send } from 'lucide-react';
import type { Invoice, InvoicePaperSize, OrderWithItems } from '@tailoring-crm/shared';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  useToast,
} from '@tailoring-crm/ui';
import { useAuth } from '../../lib/auth/auth-context.js';
import {
  useCreditInvoice,
  useInvoiceDownload,
  useIssueInvoice,
  useOrderInvoices,
  useSendInvoice,
} from '../../lib/invoices/use-invoices.js';
import { useShareInvoiceByHand } from '../../lib/invoices/use-share-invoice.js';
import { formatCurrency, formatDate } from '../../lib/format.js';

/**
 * The bill, on the screen the counter already has open.
 *
 * The card says one of two words throughout — "Tax invoice" for a registered shop, "Bill" for
 * one below the threshold — and it takes that from the API rather than deciding for itself,
 * because it is the same flag the document is issued under and two sources would eventually
 * disagree on a screen a customer can see.
 *
 * There is no edit button and there never will be: an issued document is written once. The
 * correction is a credit note, which is the owner's alone (the API says so too, and hiding the
 * button only spares a manager a request that would 403).
 *
 * A tailor sees none of this — invoicing is money, and the matrix refuses them every route.
 */
export function InvoiceCard({ order }: { order: OrderWithItems }): ReactElement | null {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { role } = useAuth();

  const invoices = useOrderInvoices(role === 'tailor' ? null : order.id);
  const issue = useIssueInvoice(order.id);
  const credit = useCreditInvoice();
  const send = useSendInvoice();
  const download = useInvoiceDownload();
  const byHand = useShareInvoiceByHand();

  const [crediting, setCrediting] = useState<Invoice | null>(null);
  const [reason, setReason] = useState('');

  if (role === 'tailor') return null;

  const view = invoices.data;
  const registered = view?.registered ?? false;
  const documents = view?.invoices ?? [];
  const live = documents.find(
    (document) => document.kind === 'invoice' && document.cancelledAt === null,
  );

  const report = (error: unknown): void => {
    toast({
      title: error instanceof Error ? error.message : t('error.unexpected'),
      variant: 'destructive',
    });
  };

  const onIssue = async (): Promise<void> => {
    try {
      const issued = await issue.mutateAsync();
      toast({ title: t('invoices.issued', { number: issued.number }), variant: 'success' });
    } catch (error) {
      report(error);
    }
  };

  /**
   * Fetch a fresh link and open it. The link is short-lived and the file is re-rendered on the
   * way, so what prints carries the balance as it stands right now rather than as it stood when
   * the bill was issued.
   */
  const onOpen = async (invoice: Invoice, paperSize: InvoicePaperSize): Promise<void> => {
    try {
      const link = await download.mutateAsync({ invoiceId: invoice.id, paperSize });
      window.open(link.url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      report(error);
    }
  };

  const onSend = async (invoice: Invoice): Promise<void> => {
    try {
      await send.mutateAsync({ invoiceId: invoice.id });
      toast({ title: t('invoices.sent'), variant: 'success' });
    } catch (error) {
      report(error);
    }
  };

  const onCredit = async (): Promise<void> => {
    if (!crediting) return;
    try {
      const note = await credit.mutateAsync({
        invoiceId: crediting.id,
        reason: reason.trim(),
      });
      toast({ title: t('invoices.credited', { number: note.number }), variant: 'success' });
      setCrediting(null);
      setReason('');
    } catch (error) {
      report(error);
    }
  };

  return (
    <section className="flex flex-col gap-3 rounded-lg border border-border p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold">
            {registered ? t('invoices.headingTax') : t('invoices.headingPlain')}
          </h3>
          <p className="text-sm text-muted-foreground">
            {live
              ? t('invoices.issuedOn', {
                  number: live.number,
                  date: formatDate(live.issuedAt),
                })
              : t('invoices.notYetIssued')}
          </p>
        </div>

        {!live && !order.cancelledAt ? (
          <Button size="sm" disabled={issue.isPending} onClick={() => void onIssue()}>
            <FileText aria-hidden="true" />
            {registered ? t('invoices.issueTax') : t('invoices.issuePlain')}
          </Button>
        ) : null}
      </div>

      {documents.length > 0 ? (
        <ul className="flex flex-col gap-2">
          {documents.map((invoice) => (
            <li
              key={invoice.id}
              className="flex flex-wrap items-start justify-between gap-2 rounded-md border border-border p-2 text-sm"
            >
              <div className="min-w-0">
                <p className="flex flex-wrap items-center gap-2 font-medium">
                  <span className={invoice.cancelledAt ? 'line-through' : undefined}>
                    {invoice.number}
                  </span>
                  <span className="tabular-nums">{formatCurrency(invoice.totals.totalPaise)}</span>
                  {invoice.kind === 'credit_note' ? (
                    <Badge variant="warning">{t('invoices.creditNote')}</Badge>
                  ) : null}
                  {invoice.cancelledAt ? (
                    <Badge variant="warning">{t('invoices.cancelledBadge')}</Badge>
                  ) : null}
                  {invoice.registered ? (
                    <Badge variant="secondary">
                      {invoice.interState ? t('invoices.igst') : t('invoices.cgstSgst')}
                    </Badge>
                  ) : null}
                </p>
                <p className="text-xs text-muted-foreground">
                  {formatDate(invoice.issuedAt)}
                  {invoice.issuedByName ? ` · ${invoice.issuedByName}` : ''}
                  {invoice.sentOnWhatsappAt ? ` · ${t('invoices.sentBadge')}` : ''}
                </p>
                {invoice.registered ? (
                  <p className="text-xs text-muted-foreground">
                    {t('invoices.taxLine', {
                      taxable: formatCurrency(invoice.totals.taxablePaise),
                      tax: formatCurrency(invoice.totals.taxPaise),
                    })}
                  </p>
                ) : null}
                {invoice.reason ? (
                  <p className="text-xs text-muted-foreground">
                    {t('invoices.creditReason', { reason: invoice.reason })}
                  </p>
                ) : null}
              </div>

              <div className="flex shrink-0 flex-wrap gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={t('invoices.downloadA4', { number: invoice.number })}
                  disabled={download.isPending}
                  onClick={() => void onOpen(invoice, 'a4')}
                >
                  <Download aria-hidden="true" />
                </Button>
                {/* Many counters have an 80mm till printer and nothing else. */}
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={t('invoices.printThermal', { number: invoice.number })}
                  disabled={download.isPending}
                  onClick={() => void onOpen(invoice, 'thermal')}
                >
                  <Printer aria-hidden="true" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={t('invoices.sendFor', { number: invoice.number })}
                  disabled={send.isPending}
                  onClick={() => void onSend(invoice)}
                >
                  <Send aria-hidden="true" />
                </Button>
                {/* From the shop's own phone: the chat opens and the PDF downloads to attach.
                    The only route for a shop without Relay, where the button above refuses. */}
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={t('invoices.shareByHandFor', { number: invoice.number })}
                  disabled={byHand.isPending}
                  onClick={() => void byHand.share(invoice).catch(report)}
                >
                  <MessageCircle aria-hidden="true" />
                </Button>
                {invoice.kind === 'invoice' && !invoice.cancelledAt && role === 'owner' ? (
                  <Button variant="ghost" size="sm" onClick={() => setCrediting(invoice)}>
                    {t('invoices.credit')}
                  </Button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      {/* A credit note without a stated reason is the argument it exists to end. */}
      <Dialog open={crediting !== null} onOpenChange={(open) => !open && setCrediting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t('invoices.creditTitle', { number: crediting?.number ?? '' })}
            </DialogTitle>
            <DialogDescription>{t('invoices.creditHint')}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="credit-reason">{t('invoices.creditReasonLabel')}</Label>
            <Input
              id="credit-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCrediting(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              disabled={reason.trim().length < 3 || credit.isPending}
              onClick={() => void onCredit()}
            >
              {t('invoices.creditConfirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

export default InvoiceCard;
